import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Hotel, Utensils, TreePine, ArrowRight } from 'lucide-react';
import hotelImage from '@/assets/split-hotel.jpg';
import restaurantImage from '@/assets/split-restaurant.jpg';
import campingImage from '@/assets/split-camping.jpg';

type SectorType = 'hotel' | 'restaurant' | 'camping' | null;

const sectors = [
  {
    id: 'restaurant' as const,
    icon: Utensils,
    title: 'Restaurant',
    description: 'Menu QR digital, réservations en ligne, site vitrine & marketing automatisé',
    image: restaurantImage,
    alt: 'Fine dining restaurant',
    link: '/restaurants',
  },
  {
    id: 'hotel' as const,
    icon: Hotel,
    title: 'Hôtel',
    description: 'Room service digital, QR codes par chambre, portail client & conciergerie connectée',
    image: hotelImage,
    alt: 'Luxury hotel lobby',
    link: '/hotels',
  },
  {
    id: 'camping' as const,
    icon: TreePine,
    title: 'Camping',
    description: 'Snack-bar digital, planning animations, QR par emplacement & portail vacanciers',
    image: campingImage,
    alt: 'Mediterranean camping resort',
    link: '/campings',
  },
];

const SplitChoicePage = () => {
  const navigate = useNavigate();
  const [hovered, setHovered] = useState<SectorType>(null);

  const getFlexValue = (sectorId: SectorType) => {
    if (!hovered) return 1;
    if (hovered === sectorId) return 1.6;
    return 0.5;
  };

  return (
    <div className="h-screen w-screen flex flex-col md:flex-row overflow-hidden relative bg-black">
      {sectors.map((sector, index) => (
        <div key={sector.id} className="contents">
          {/* Divider between sectors */}
          {index > 0 && (
            <>
              <div className="hidden md:block w-[2px] bg-white/20 z-20 relative">
                {index === 1 && (
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 backdrop-blur-xl border border-white/30 flex items-center justify-center">
                    <span className="text-white font-bold text-xs">OU</span>
                  </div>
                )}
              </div>
              <div className="md:hidden h-[2px] bg-white/20 z-20 relative">
                {index === 1 && (
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 backdrop-blur-xl border border-white/30 flex items-center justify-center">
                    <span className="text-white font-bold text-xs">OU</span>
                  </div>
                )}
              </div>
            </>
          )}

          {/* Sector panel */}
          <motion.div
            className="relative flex-1 cursor-pointer overflow-hidden"
            animate={{ flex: getFlexValue(sector.id) }}
            transition={{ duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
            onMouseEnter={() => setHovered(sector.id)}
            onMouseLeave={() => setHovered(null)}
            onClick={() => navigate(sector.link)}
          >
            <img
              src={sector.image}
              alt={sector.alt}
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-black/40 hover:bg-black/30 transition-colors duration-500" />

            <div className="relative z-10 flex flex-col items-center justify-center h-full px-6 text-center">
              <motion.div
                animate={{ scale: hovered === sector.id ? 1.1 : 1 }}
                transition={{ duration: 0.4 }}
              >
                <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center mb-4 md:mb-6">
                  <sector.icon className="w-8 h-8 md:w-10 md:h-10 text-white" />
                </div>
              </motion.div>

              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-3 tracking-tight">
                {sector.title}
              </h2>
              <p className="text-white/70 text-sm md:text-lg max-w-xs md:max-w-md mb-6 leading-relaxed">
                {sector.description}
              </p>

              <motion.div
                className="flex items-center gap-2 text-white/90 font-medium text-base md:text-lg"
                animate={{
                  x: hovered === sector.id ? 8 : 0,
                  opacity: hovered === sector.id ? 1 : 0.7,
                }}
                transition={{ duration: 0.3 }}
              >
                Découvrir <ArrowRight className="w-5 h-5" />
              </motion.div>
            </div>
          </motion.div>
        </div>
      ))}

      {/* Logo watermark */}
      <div className="absolute top-6 left-1/2 -translate-x-1/2 z-30">
        <span className="text-white/90 font-bold text-2xl tracking-wide">
          🍽 miam<span className="text-orange-400">.food</span>
        </span>
      </div>

      {/* Bottom tagline */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-30">
        <p className="text-white/50 text-sm tracking-widest uppercase">
          Une plateforme · Trois univers
        </p>
      </div>
    </div>
  );
};

export default SplitChoicePage;
